import { useTheme } from '../ThemeContext';
import { useState, useEffect, useRef } from 'react';
import emailjs from 'emailjs-com';

// ── Exact credentials from working portfolio ─────────────
const EJS_SERVICE_ID  = 'service_77tpu5b';   // DEFAULT Gmail service
const EJS_TEMPLATE_ID = 'template_2vtfs46';  // Contact Us template
const EJS_PUBLIC_KEY  = 'CTKtIbWNKL7aEJ02v'; // capital I (matches portfolio)
// ─────────────────────────────────────────────────────────

const DEVELOPERS = [
  { name:'Prikshit Gautam', role:'Research Contributor & Full-Stack AI Developer', desc:'Built the Hybrid LSTM model, Flask backend (ONNX Runtime), and React frontend. Specialized in 5G network AI and cloud deployment with Intensive Contribution to research methodology, dataset analysis, and model validation on the KTH ExPECA testbed data.', skills:['Python','TensorFlow','ONNX','React','Flask','Data Analysis','5G Research','LSTM','Python','Network Protocols'], github:null, linkedin:'https://www.linkedin.com/in/prikshit-g-76b1a9308/', initials:'PG', color:'#00d4ff' },
  { name:'Kartik Bansal', role:'Collaborator & Research Contributor', desc:'Contributed to research methodology, dataset analysis, and model validation on the KTH ExPECA testbed data.', skills:['Data Analysis','5G Research','LSTM','Python','Network Protocols'], github:null, linkedin:'https://www.linkedin.com/in/kartik-bansal-a33084275/', initials:'KB', color:'#00cc66' },
];

const CONTACT_ITEMS = [
  {   label:'Email',    value:'pgautamlinkedin@gmail.com', href:'mailto:pgautamlinkedin@gmail.com' },
  {   label:'Phone',    value:'(+91) 9877035742',           href:'tel:+919877035742' },
  {   label:'LinkedIn', value:'Prikshit Gautam',            href:'https://www.linkedin.com/in/prikshit-gautam-76b1a9308/' },
];

export default function Contact() {
  const { dark } = useTheme();
  const [form,   setForm]   = useState({ name:'', email:'', message:'' });
  const [status, setStatus] = useState('idle'); // idle | sending | success | error
  const [errMsg, setErrMsg] = useState('');
  const [inView, setInView] = useState({});
  const refs = useRef({});

  useEffect(() => {
    const obs = new IntersectionObserver(entries => {
      entries.forEach(e => { if(e.isIntersecting) setInView(p=>({...p,[e.target.dataset.c]:true})); });
    }, {threshold:0.1});
    Object.values(refs.current).forEach(el => el && obs.observe(el));
    return () => obs.disconnect();
  }, []);

  const ref = k => el => { refs.current[k]=el; };

  const bg       = dark?'#060c18':'#f4f8ff';
  const border   = dark?'rgba(0,212,255,0.13)':'rgba(0,120,200,0.14)';
  const accent   = dark?'#00d4ff':'#0078c8';
  const text1    = dark?'#e8f4ff':'#0a1e3d';
  const text2    = dark?'#8ab4d4':'#2a4a6a';
  const text3    = dark?'#4a7090':'#5878a0';
  const gridC    = dark?'rgba(0,212,255,0.04)':'rgba(0,120,200,0.05)';
  const cardBg   = dark?'#0d1929':'#ffffff';
  const cardShad = dark?'0 2px 20px rgba(0,0,0,0.4)':'0 4px 24px rgba(0,100,200,0.09)';
  const inputBg  = dark?'rgba(0,212,255,0.04)':'#fff';
  const inputBd  = dark?'rgba(0,212,255,0.18)':'rgba(0,120,200,0.22)';

  const handleChange = e => setForm(p=>({...p,[e.target.name]:e.target.value}));

  // Uses emailjs-com SDK — same as portfolio (avoids fetch CORS issues)
  const handleSubmit = e => {
    e.preventDefault();
    if(!form.name.trim()||!form.email.trim()||!form.message.trim()) return;
    setStatus('sending'); setErrMsg('');

    emailjs.send(
      EJS_SERVICE_ID,
      EJS_TEMPLATE_ID,
      {
        name:    form.name,
        email:   form.email,
        message: form.message,
      },
      EJS_PUBLIC_KEY
    ).then(
      () => {
        setStatus('success');
        setForm({name:'',email:'',message:''});
        setTimeout(()=>setStatus('idle'), 6000);
      },
      (error) => {
        setErrMsg(error?.text || 'Failed to send. Please email directly at pgautamlinkedin@gmail.com');
        setStatus('error');
        setTimeout(()=>setStatus('idle'), 8000);
      }
    );
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Orbitron:wght@400;700;900&family=Inter:wght@400;500;600;700&display=swap');
        *{box-sizing:border-box;}
        .ct-root{background:${bg};color:${text1};font-family:'Inter',sans-serif;min-height:100vh;padding-top:60px;transition:background 0.35s;}
        .ct-root::before{content:'';position:fixed;inset:0;background-image:linear-gradient(${gridC} 1px,transparent 1px),linear-gradient(90deg,${gridC} 1px,transparent 1px);background-size:52px 52px;pointer-events:none;z-index:0;}
        .fade-in{opacity:0;transform:translateY(22px);transition:opacity 0.6s ease,transform 0.6s ease;}
        .fade-in.vis{opacity:1;transform:none;}
        .ct-input{width:100%;background:${inputBg};border:1px solid ${inputBd};border-radius:6px;padding:12px 14px;font-size:14px;color:${text1};font-family:'Inter',sans-serif;outline:none;transition:border-color 0.2s,box-shadow 0.2s;}
        .ct-input::placeholder{color:${text3};}
        .ct-input:focus{border-color:${accent};box-shadow:0 0 0 3px ${dark?'rgba(0,212,255,0.1)':'rgba(0,120,200,0.1)'};}
        .ct-textarea{resize:vertical;min-height:130px;}
        .ct-btn{width:100%;background:${accent};color:${dark?'#020810':'#fff'};font-weight:700;font-size:13px;letter-spacing:1px;text-transform:uppercase;border:none;border-radius:6px;padding:14px;cursor:none;transition:all 0.2s;font-family:'Inter',sans-serif;}
        .ct-btn:hover:not(:disabled){filter:brightness(1.1);transform:translateY(-1px);}
        .ct-btn:disabled{opacity:0.55;cursor:not-allowed;}
        .ct-label{display:block;font-size:11px;font-weight:700;letter-spacing:1.5px;color:${text3};text-transform:uppercase;margin-bottom:6px;}
        .skill-pill{font-size:11px;font-weight:600;padding:3px 10px;border-radius:12px;background:${dark?'rgba(0,212,255,0.07)':'rgba(0,120,200,0.07)'};border:1px solid ${dark?'rgba(0,212,255,0.2)':'rgba(0,120,200,0.2)'};color:${accent};letter-spacing:0.5px;}
        .social-btn{display:inline-flex;align-items:center;gap:7px;font-size:12px;font-weight:600;letter-spacing:0.5px;padding:8px 16px;border-radius:6px;border:1px solid ${border};background:${dark?'rgba(255,255,255,0.04)':'rgba(0,0,0,0.03)'};color:${text1};text-decoration:none;transition:all 0.2s;cursor:none;}
        .social-btn:hover{border-color:${accent};color:${accent};background:${dark?'rgba(0,212,255,0.06)':'rgba(0,120,200,0.06)'};}
        @media(max-width:768px){.ct-grid{grid-template-columns:1fr!important;}.dev-grid{grid-template-columns:1fr!important;}}
      `}</style>

      <div className="ct-root">

        {/* Hero */}
        <div style={{position:'relative',zIndex:1,padding:'60px 24px 40px',textAlign:'center',borderBottom:`1px solid ${border}`,background:dark?'rgba(0,212,255,0.02)':'rgba(0,120,200,0.02)'}}>
          <div style={{background:dark?'rgba(0,212,255,0.07)':'rgba(0,120,200,0.07)',border:`1px solid ${border}`,display:'inline-block',padding:'5px 18px',borderRadius:20,marginBottom:20}}>
            <span style={{fontSize:11,fontWeight:600,letterSpacing:3,color:accent,textTransform:'uppercase'}}>Contact</span>
          </div>
          <h1 style={{fontFamily:"'Orbitron',monospace",fontSize:'clamp(24px,5vw,48px)',fontWeight:900,color:text1,marginBottom:12,lineHeight:1.08}}>
            Get In Touch<br/><span style={{color:accent}}>With the Team</span>
          </h1>
          <p style={{fontSize:16,color:text2,maxWidth:500,margin:'0 auto',lineHeight:1.7}}>
            Questions about the model, research collaboration, or deployment? We'd love to hear from you.
          </p>
        </div>

        <div style={{position:'relative',zIndex:1,maxWidth:1060,margin:'0 auto',padding:'60px 24px 80px'}}>

          {/* Team */}
          <div data-c="devs" ref={ref('devs')}>
            <div className={`fade-in${inView.devs?' vis':''}`}>
              <div style={{fontSize:11,fontWeight:700,letterSpacing:3,color:text3,textTransform:'uppercase',marginBottom:16,display:'flex',alignItems:'center',gap:12}}>
                01 · The Team <div style={{flex:1,height:1,background:border}}/>
              </div>
              <div className="dev-grid" style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:20,marginBottom:56}}>
                {DEVELOPERS.map((dev,i)=>(
                  <div key={dev.name} style={{border:`1px solid ${dev.color}22`,background:cardBg,borderRadius:10,padding:28,boxShadow:cardShad,position:'relative',overflow:'hidden',transition:'all 0.3s',transitionDelay:`${i*0.1}s`}}
                    onMouseEnter={e=>{e.currentTarget.style.borderColor=dev.color+'55';e.currentTarget.style.transform='translateY(-3px)';}}
                    onMouseLeave={e=>{e.currentTarget.style.borderColor=dev.color+'22';e.currentTarget.style.transform='none';}}
                  >
                    <div style={{position:'absolute',top:0,left:0,right:0,height:2,background:`linear-gradient(90deg,${dev.color},transparent)`}}/>
                    <div style={{display:'flex',alignItems:'flex-start',gap:16,marginBottom:16}}>
                      <div style={{width:52,height:52,borderRadius:12,background:`${dev.color}18`,border:`2px solid ${dev.color}44`,display:'flex',alignItems:'center',justifyContent:'center',fontFamily:"'Orbitron',monospace",fontSize:14,fontWeight:900,color:dev.color,flexShrink:0}}>{dev.initials}</div>
                      <div>
                        <div style={{fontFamily:"'Orbitron',monospace",fontSize:14,fontWeight:700,color:text1,marginBottom:4}}>{dev.name}</div>
                        <div style={{fontSize:12,color:dev.color,fontWeight:600,letterSpacing:0.5}}>{dev.role}</div>
                      </div>
                    </div>
                    <p style={{fontSize:13,color:text2,lineHeight:1.7,marginBottom:16}}>{dev.desc}</p>
                    <div style={{display:'flex',flexWrap:'wrap',gap:6,marginBottom:18}}>
                      {dev.skills.map(s=><span key={s} className="skill-pill">{s}</span>)}
                    </div>
                    <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
                      {dev.github&&(<a href={dev.github} target="_blank" rel="noreferrer" className="social-btn"><svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0 0 24 12c0-6.63-5.37-12-12-12z"/></svg>GitHub</a>)}
                      <a href={dev.linkedin} target="_blank" rel="noreferrer" className="social-btn"><svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>LinkedIn</a>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Form */}
          <div data-c="form" ref={ref('form')}>
            <div className={`fade-in${inView.form?' vis':''}`}>
              <div style={{fontSize:11,fontWeight:700,letterSpacing:3,color:text3,textTransform:'uppercase',marginBottom:16,display:'flex',alignItems:'center',gap:12}}>
                02 · Send a Message <div style={{flex:1,height:1,background:border}}/>
              </div>
              <div className="ct-grid" style={{display:'grid',gridTemplateColumns:'1fr 1.6fr',gap:32}}>

                {/* Left info */}
                <div style={{display:'flex',flexDirection:'column',gap:14}}>
                  <div style={{fontFamily:"'Orbitron',monospace",fontSize:18,fontWeight:700,color:text1,marginBottom:4}}>Let's Connect</div>
                  <p style={{fontSize:14,color:text2,lineHeight:1.75,marginBottom:4}}>
                    Whether you're interested in the research, want to integrate the delay prediction API, or just have questions about 5G networks — reach out.
                  </p>
                  {CONTACT_ITEMS.map(item=>(
                    <a key={item.label} href={item.href} target="_blank" rel="noreferrer"
                      style={{display:'flex',alignItems:'center',gap:14,padding:'14px 16px',background:cardBg,border:`1px solid ${border}`,borderRadius:8,textDecoration:'none',transition:'all 0.2s',boxShadow:cardShad}}
                      onMouseEnter={e=>{e.currentTarget.style.borderColor=accent+'55';}}
                      onMouseLeave={e=>{e.currentTarget.style.borderColor=border;}}
                    >
                      <span style={{fontSize:20}}>{item.icon}</span>
                      <div>
                        <div style={{fontSize:10,fontWeight:700,letterSpacing:1.5,color:text3,textTransform:'uppercase',marginBottom:2}}>{item.label}</div>
                        <div style={{fontSize:13,color:text1,fontWeight:600}}>{item.value}</div>
                      </div>
                    </a>
                  ))}
                </div>

                {/* Right form */}
                <div style={{background:cardBg,border:`1px solid ${border}`,borderRadius:10,padding:32,boxShadow:cardShad,position:'relative',overflow:'hidden'}}>
                  <div style={{position:'absolute',top:0,left:0,right:0,height:2,background:`linear-gradient(90deg,${accent},transparent)`}}/>
                  <form onSubmit={handleSubmit} style={{display:'flex',flexDirection:'column',gap:16}}>
                    <div>
                      <label className="ct-label">Your Name</label>
                      <input name="name" value={form.name} onChange={handleChange} placeholder="John Smith" className="ct-input" required disabled={status==='sending'}/>
                    </div>
                    <div>
                      <label className="ct-label">Email Address</label>
                      <input name="email" type="email" value={form.email} onChange={handleChange} placeholder="john@example.com" className="ct-input" required disabled={status==='sending'}/>
                    </div>
                    <div>
                      <label className="ct-label">Message</label>
                      <textarea name="message" value={form.message} onChange={handleChange} placeholder="Your message here..." className="ct-input ct-textarea" required disabled={status==='sending'}/>
                    </div>
                    <button type="submit" className="ct-btn" disabled={status==='sending'||!form.name||!form.email||!form.message}>
                      {status==='sending'?'⏳ Sending...':status==='success'?'✅ Message Sent!':'🚀 Send Message'}
                    </button>
                    {status==='success'&&(
                      <div style={{background:dark?'rgba(0,204,102,0.08)':'rgba(0,160,80,0.06)',border:'1px solid rgba(0,204,102,0.3)',borderRadius:6,padding:'12px 16px',fontSize:13,color:'#00cc66',textAlign:'center'}}>
                        ✅ Message delivered! We'll get back to you soon.
                      </div>
                    )}
                    {status==='error'&&(
                      <div style={{background:dark?'rgba(255,68,85,0.08)':'rgba(255,68,85,0.06)',border:'1px solid rgba(255,68,85,0.3)',borderRadius:6,padding:'12px 16px',fontSize:13,color:'#ff4455'}}>
                        ❌ {errMsg||'Something went wrong. Please email directly at pgautamlinkedin@gmail.com'}
                      </div>
                    )}
                  </form>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </>
  );
}
